/**
 * ValidationStateUtils.ts
 *
 * Centralized validation state management for ReportDataTable component.
 * Uses a single sessionStorage key instead of multiple keys for:
 * - Better data consistency (single source of truth)
 * - Easier debugging and maintenance
 * - Reduced risk of state drift between keys
 */

// Storage key constant
export const VALIDATION_STATE_KEY = 'validationState';

// Legacy keys (for migration/cleanup)
export const LEGACY_KEYS = ['lastApiResponse', 'validationHasErrors', 'finalReviewData'] as const;

/**
 * Error information for a single field
 */
export interface ReportError {
  fieldName: string;
  error: string;
}

/**
 * Data structure for a single report/file
 */
export interface ReportData {
  nameOfReport: string;
  callNumber: string;
  errors: string;
  status?: string;
}

/**
 * Consolidated validation state - single source of truth
 */
export interface ValidationState {
  /** The array of report data from API response */
  data: ReportData[];
  /** Whether any reports have errors */
  hasErrors: boolean;
  /** Timestamp when state was created/updated */
  timestamp: number;
}

/**
 * Safely parse and validate API response data
 * @param response - Raw API response
 * @returns Normalized array of ReportData
 */
export const normalizeApiResponse = (response: unknown): ReportData[] => {
  // Handle response.data or direct array
  const rawData = (response as any)?.data ?? response;

  if (!Array.isArray(rawData)) {
    return [];
  }

  // Validate and normalize each item
  return rawData.map((item: any) => ({
    nameOfReport: String(item?.nameOfReport ?? ''),
    callNumber: String(item?.callNumber ?? ''),
    errors: String(item?.errors ?? ''),
    status: String(item?.status ?? '')
  }));
};

/**
 * Check if any reports have errors
 * @param data - Array of report data
 * @returns true if any report has errors
 */
export const checkHasErrors = (data: ReportData[]): boolean => {
  return data.some(report => typeof report.errors === 'string' && report.errors.trim().length > 0);
};

/**
 * Create a ValidationState object from API response
 * @param response - Raw API response
 * @returns ValidationState object
 */
export const createValidationState = (response: unknown): ValidationState => {
  const data = normalizeApiResponse(response);
  return {
    data,
    hasErrors: checkHasErrors(data),
    timestamp: Date.now()
  };
};

/**
 * Save validation state to sessionStorage
 * @param state - ValidationState to save
 */
export const saveValidationState = (state: ValidationState): void => {
  try {
    sessionStorage.setItem(VALIDATION_STATE_KEY, JSON.stringify(state));
  } catch (e) {
    console.error('ValidationState: Failed to save to sessionStorage', e);
  }
};

/**
 * Load validation state from sessionStorage
 * @returns ValidationState or null if not found/invalid
 */
export const loadValidationState = (): ValidationState | null => {
  try {
    const stored = sessionStorage.getItem(VALIDATION_STATE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored);

    // Validate structure
    if (!parsed || !Array.isArray(parsed.data)) {
      return null;
    }

    return {
      data: parsed.data,
      hasErrors: Boolean(parsed.hasErrors),
      timestamp: Number(parsed.timestamp) || Date.now()
    };
  } catch (e) {
    console.error('ValidationState: Failed to load from sessionStorage', e);
    return null;
  }
};

/**
 * Update validation state with new data (e.g., after removing a report)
 * @param data - Updated array of report data
 */
export const updateValidationState = (data: ReportData[]): void => {
  const state: ValidationState = {
    data,
    hasErrors: checkHasErrors(data),
    timestamp: Date.now()
  };
  saveValidationState(state);
};

/**
 * Clear validation state from sessionStorage
 */
export const clearValidationState = (): void => {
  sessionStorage.removeItem(VALIDATION_STATE_KEY);
};

/**
 * Clean up legacy sessionStorage keys
 * Call this after migration to ensure old keys don't cause issues
 */
export const cleanupLegacyKeys = (): void => {
  LEGACY_KEYS.forEach(key => {
    sessionStorage.removeItem(key);
  });
};

/**
 * Get hasErrors flag from current state
 * @returns boolean indicating if validation has errors
 */
export const getHasErrors = (): boolean => {
  const state = loadValidationState();
  return state?.hasErrors ?? false;
};

/**
 * Get data from current state
 * @param fallback - Fallback data if state not found
 * @returns Array of ReportData
 */
export const getData = (fallback: ReportData[] = []): ReportData[] => {
  const state = loadValidationState();
  return state?.data ?? fallback;
};